import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { Upload } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useRevalidateOnLanguageChange } from '@/i18n/use-revalidate-on-language-change';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';
import { type DataUpload, DataUploadType } from '@/common/data-upload';
import { Button } from '@/components/ui/button';
import { DialogClose, DialogFooter } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@/queries/common';
import { addDataUpload } from '@/queries/data-uploads/add-data-upload';
import { queryClient } from '@/queries/query.client';

import { Spinner } from '../../ui/spinner';
import { Switch } from '../../ui/switch';
import { type Add3DDataPanelProps, AssetStructureField, NameField } from './Add3DDataFields';

const buildCesiumIdSchema = (t: TFunction) =>
	z.object({
		cesiumId: z.string().min(1, t('files.validation.cesiumIdRequired')),
		subSurfaceMode: z.boolean(),
	});
type CesiumIdFormValues = z.infer<ReturnType<typeof buildCesiumIdSchema>>;

/**
 * "Cesium ION" panel of Add3DDataDialog: records an asset ID for a tileset that
 * already lives in a Cesium ION account. Nothing is transferred — TilesLoader
 * picks the record up and loads it with Cesium3DTileset.fromIonAssetId.
 */
export function CesiumId({
	onAdd,
	onClose,
	assetId,
	inspectionId,
	damageId,
	name,
	setName,
	assetStructure,
}: Add3DDataPanelProps) {
	const { t } = useTranslation();
	const formSchema = useMemo(() => buildCesiumIdSchema(t), [t]);
	const form = useForm<CesiumIdFormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			cesiumId: '',
			subSurfaceMode: false,
		},
	});
	useRevalidateOnLanguageChange(form);

	const addDataUploadsApi = useMutation({
		mutationFn: async (data: Partial<DataUpload>) => addDataUpload(data),
		onSuccess: async () => {
			toast.success(t('files.toast.cesiumAdded'));
			// Invalidate and refetch data uploads list
			await queryClient.invalidateQueries({ queryKey: ['data-uploads', 'list'] });
			onAdd?.();
		},
	});

	const onSubmit = async (values: CesiumIdFormValues) => {
		try {
			await addDataUploadsApi.mutateAsync({
				cesiumId: values.cesiumId,
				name,
				assetId,
				fileType: DataUploadType.CESIUM_ID,
				uploadType: DataUploadType.CESIUM_ID,
				subAssetId:
					assetStructure.selectedId && assetStructure.selectedId !== assetId ? assetStructure.selectedId : undefined,
				inspectionId: inspectionId || undefined,
				damageId: damageId || undefined,
				subSurfaceMode: values.subSurfaceMode || false,
			});
			onClose();
		} catch (error) {
			console.log('Add Cesium ID error:', error);
			toast.error(t('files.toast.cesiumAddFailed'), { description: apiErrorMessage(error) });
		}
	};

	const isSubmitting = addDataUploadsApi.isPending;

	return (
		<Form {...form}>
			<form noValidate onSubmit={form.handleSubmit(onSubmit)}>
				<div className="space-y-4 py-4">
					<FormField
						control={form.control}
						name="cesiumId"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t('files.upload.cesiumIdLabel')} <span className="text-red-500">*</span>
								</FormLabel>
								<FormControl>
									<Input
										type="text"
										className="h-10 rounded-md px-3 py-2 text-sm bg-white"
										placeholder={t('files.upload.cesiumIdPlaceholder')}
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<NameField
						value={name}
						onChange={setName}
						label={t('files.upload.fileNameLabel')}
						placeholder={t('files.upload.fileNamePlaceholder')}
					/>

					<AssetStructureField state={assetStructure} required />

					<FormField
						control={form.control}
						name="subSurfaceMode"
						render={({ field }) => (
							<FormItem className="flex items-center space-x-3 space-y-0">
								<FormLabel htmlFor="subSurfaceMode">{t('files.upload.subSurfaceModeLabel')}</FormLabel>
								<FormControl>
									<Switch
										id={'subSurfaceMode'}
										name={field.name}
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
					<DialogClose asChild className="h-10 px-4 py-2">
						<Button variant="outline">{t('common.actions.cancel')}</Button>
					</DialogClose>
					<Button type="submit" className="h-10 px-4 py-2" disabled={isSubmitting || !name.trim()}>
						{!isSubmitting && (
							<>
								<Upload className="h-4 w-4" />
								{t('common.actions.add')}
							</>
						)}
						{isSubmitting && <Spinner className="ml-2" />}
						{isSubmitting && t('files.upload.adding')}
					</Button>
				</DialogFooter>
			</form>
		</Form>
	);
}
